<template>
    <div class="min-h-screen bg-white">
        <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
            <Loader size="large" />
        </div>
        <div v-else-if="loadError === 'error'" class="flex justify-center items-center min-h-[400px]">
            <div class="text-center">
                <h2 class="text-2xl font-marketing font-bold text-primary mb-4">We couldn't load this book</h2>
                <p class="text-gray-600 font-sans mb-6">Something went wrong. Please check your connection and try again.</p>
                <button @click="reload"
                    class="px-6 py-3 rounded-md bg-accent text-white font-marketing font-semibold hover:bg-green-600 transition-colors">
                    Try Again
                </button>
            </div>
        </div>
        <div v-else-if="!book" class="flex justify-center items-center min-h-[400px]">
            <div class="text-center">
                <h2 class="text-2xl font-marketing font-bold text-primary mb-4">Book Not Found</h2>
                <p class="text-gray-600 font-sans mb-6">This title is no longer available. It may have been removed from the store.</p>
                <a href="/books" class="text-accent hover:text-green-600 font-marketing font-semibold underline">
                    Browse All Books
                </a>
            </div>
        </div>
        <div v-else class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5">
            <div v-if="book.isDeleted"
                class="mb-3 px-4 py-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 font-sans text-sm">
                The publisher no longer offers this title. If you already purchased it, you can still access it in your
                <a href="/library" class="font-marketing font-semibold underline">library</a>.
            </div>
            <div v-else-if="book.isUnavailable"
                class="mb-3 px-4 py-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 font-sans text-sm">
                This title is currently unavailable to new buyers. If you already purchased it, you can still access it in your
                <a href="/library" class="font-marketing font-semibold underline">library</a>.
            </div>
            <div class="grid grid-cols-1 xl:grid-cols-12 gap-1 mb-2">
                <div class="xl:col-span-4">
                    <div class="sticky top-8">
                        <div :style="getBookImageStyle()"
                            class="bg-creme-200 rounded-none overflow-hidden shadow-lg mb-1">
                            <div v-if="book.coverImage" class="w-full h-full">
                                <img :src="book.coverImage" :alt="book.title"
                                    class="w-full h-auto min-h-full object-cover" />
                            </div>
                            <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
                                <svg class="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="xl:col-span-8">
                    <div v-if="book.bestseller" class="mb-0.5">
                        <span
                            class="inline-block bg-accent text-white px-3 py-1 rounded text-sm font-marketing font-semibold">
                            Best Seller
                        </span>
                    </div>

                    <div v-if="book.isTest && book.testAccess === 'owner'"
                        class="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                        The publisher no longer lists this title — you still own it
                    </div>
                    <div v-else-if="book.isTest"
                        class="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                        ⚠ Admin-only test — not visible to customers
                    </div>
                    <h1 class="text-4xl xl:text-5xl font-marketing font-bold text-primary mb-0.5" data-no-translate>
                        {{ book.title }}
                    </h1>

                    <p v-if="book.subtitle" class="text-xl text-gray-600 mb-3 font-marketing">
                        {{ book.subtitle }}
                    </p>

                    <p class="text-lg text-primary mb-1 font-marketing" data-no-translate>
                        By {{ book.author }}
                    </p>

                    <div class="border-t border-b border-gray-200 py-1 mb-1 overflow-hidden">
                        <div class="flex flex-col gap-2">
                            <p v-if="!hasMultipleFormats" class="text-2xl font-marketing font-bold text-primary"
                                data-no-translate>
                                {{ selectedFormat.price }}
                            </p>
                            <p v-if="book.publishedDate" class="text-sm text-gray-600 font-sans">
                                Published on {{ book.publishedDate }} | {{ book.pages }} Pages
                            </p>
                            <p v-else class="text-sm text-gray-600 font-sans">
                                {{ book.pages }} Pages
                            </p>
                        </div>
                    </div>

                    <div v-if="book.isUnavailable && book.retailers && Array.isArray(book.retailers) && book.retailers.length > 0"
                        class="mb-1">
                        <p class="text-sm text-gray-600 font-sans mb-0.5">Also available from:</p>
                        <div class="flex flex-wrap gap-1">
                            <template v-for="(retailer, index) in book.retailers" :key="retailer?.id || index">
                                <a v-if="retailer && retailer.name && retailer.link" :href="retailer.link"
                                    target="_blank" rel="noopener noreferrer"
                                    class="px-4 py-2 bg-white border border-gray-300 rounded-md text-accent hover:bg-creme-200 hover:border-accent transition-colors font-marketing font-semibold text-sm"
                                    data-no-translate>
                                    {{ retailer.name }}
                                </a>
                            </template>
                        </div>
                    </div>

                    <div v-if="book.formats && book.formats.length > 0" class="mb-1">
                        <h3 class="text-lg font-marketing font-semibold text-primary mb-0.5">Available Formats</h3>
                        <div class="space-y-0.5">
                            <div v-for="format in book.formats" :key="format.id || format.type"
                                class="border border-gray-200 rounded-md overflow-hidden">
                                <button v-if="book.formats.length > 1" @click="toggleFormat(format.id || format.type)"
                                    class="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left">
                                    <div class="flex-1">
                                        <div class="flex items-center gap-3">
                                            <span class="font-marketing font-semibold text-primary">{{ format.type
                                            }}</span>
                                            <span class="text-lg font-marketing font-bold text-primary"
                                                data-no-translate>{{ format.price
                                                }}</span>
                                            <span v-if="format.discountPercentage"
                                                class="text-sm text-accent font-sans">
                                                ({{ format.discountPercentage }}% off)
                                            </span>
                                        </div>
                                    </div>
                                    <svg class="w-5 h-5 text-gray-600 transition-transform"
                                        :class="{ 'rotate-180': expandedFormats[format.id || format.type] }" fill="none"
                                        stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div v-if="book.formats.length === 1" class="px-4 py-3 bg-white">
                                    <div class="flex items-center gap-3">
                                        <span class="font-marketing font-semibold text-primary">{{ format.type }}</span>
                                        <span class="text-lg font-marketing font-bold text-primary" data-no-translate>{{
                                            format.price
                                        }}</span>
                                        <span v-if="format.discountPercentage" class="text-sm text-accent font-sans">
                                            Save {{ format.discountPercentage }}%
                                            }}</span>
                                        <span v-if="format.discountPercentage" class="text-sm text-accent font-sans">
                                            ({{ format.discountPercentage }}% off)
                                        </span>
                                    </div>
                                </div>
                                <div v-show="book.formats.length === 1 || expandedFormats[format.id || format.type]"
                                    class="px-4 pb-4 bg-white border-t border-gray-200">
                                    <dl class="grid grid-cols-1 xl:grid-cols-2 gap-3 text-sm font-sans pt-3">
                                        <div>
                                            <dt class="font-semibold text-primary mb-1">ISBN</dt>
                                            <dd class="text-gray-600">{{ format.isbn }}</dd>
                                        </div>
                                        <div>
                                            <dt class="font-semibold text-primary mb-1">Price</dt>
                                            <dd class="text-gray-600">
                                                <span v-if="format.discountPercentage"
                                                    class="line-through text-gray-400 mr-2">{{
                                                    format.originalPrice }}</span>
                                                <span>{{ format.price }}</span>
                                            </dd>
                                        </div>
                                        <div v-if="format.discountPercentage">
                                            <dt class="font-semibold text-primary mb-1">Discount</dt>
                                            <dd class="text-accent font-semibold">{{ format.discountPercentage }}% off
                                            </dd>
                                        </div>
                                    </dl>
                                    <div v-if="!book.isUnavailable && !format.isUnavailable" class="mt-4">
                                        <button v-if="!isFormatInCart(format)" @click="addFormatToCart(format)"
                                            class="mt-4 px-6 py-3 rounded-md transition-colors font-marketing text-base font-semibold flex items-center justify-center gap-2 bg-accent text-white hover:bg-green-600">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            Cart
                                        </button>
                                        <div v-else class="flex flex-col sm:flex-row gap-3 mt-4">
                                            <button disabled
                                                class="flex-1 px-6 py-3 bg-gray-300 text-gray-600 rounded-md cursor-not-allowed font-marketing text-base font-semibold flex items-center justify-center gap-2">
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                                In Cart
                                            </button>
                                            <div class="flex items-center gap-2 bg-gray-100 rounded-md p-1">
                                                <button @click="decrementFormatQuantity(format)"
                                                    class="w-10 h-10 flex items-center justify-center bg-white text-primary rounded-md hover:bg-gray-200 active:bg-gray-300 transition-colors font-bold text-lg border border-gray-300"
                                                    :disabled="getFormatQuantity(format) <= 0">
                                                    <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M20 12H4" />
                                                    </svg>
                                                </button>
                                                <span
                                                    class="w-12 text-center font-marketing font-bold text-lg text-primary">
                                                    {{ getFormatQuantity(format) }}
                                                </span>
                                                <button @click="incrementFormatQuantity(format)"
                                                    class="w-10 h-10 flex items-center justify-center bg-white text-primary rounded-md hover:bg-gray-200 active:bg-gray-300 transition-colors font-bold text-lg border border-gray-300">
                                                    <svg class="w-5 h-5" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2" d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <p v-else-if="format.isUnavailable"
                                        class="mt-3 text-sm text-gray-500 font-sans italic">
                                        This format is currently unavailable
                                    </p>
                                    <div v-if="format.sampleAvailable && format.digitalProductId" class="mt-3">
                                        <a :href="sampleHref(format)"
                                            class="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-accent text-accent hover:bg-accent hover:text-white transition-colors font-marketing text-sm font-semibold">
                                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                            {{ format.formatType === 'audiobook' ? 'Listen to free sample' : 'Read free sample' }}
                                        </a>
                                    </div>
                                    <div v-if="format.retailers && format.retailers.length > 0" class="mt-3">
                                        <p class="text-sm text-gray-600 font-sans mb-2">Also available from:</p>
                                        <div class="flex flex-wrap gap-2">
                                            <template v-for="(retailer, idx) in format.retailers"
                                                :key="retailer?.id || idx">
                                                <a v-if="retailer && retailer.name && retailer.link"
                                                    :href="retailer.link" target="_blank" rel="noopener noreferrer"
                                                    class="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-accent hover:bg-gray-50 hover:border-accent transition-colors font-marketing font-semibold text-xs"
                                                    data-no-translate>
                                                    {{ retailer.name }}
                                                </a>
                                            </template>
                                        </div>
                                    </div>

                                    <div class="mt-6 pt-6 border-t border-gray-200">
                                        <h4 class="text-lg font-marketing font-semibold text-primary mb-4">Product
                                            Details</h4>
                                        <dl class="grid grid-cols-1 xl:grid-cols-2 gap-4 text-sm font-sans">
                                            <div>
                                                <dt class="font-semibold text-primary mb-1">Published on</dt>
                                                <dd class="text-gray-600">{{ book.publishedDate || '*Date Not Provided'
                                                    }}</dd>
                                            </div>
                                            <div>
                                                <dt class="font-semibold text-primary mb-1">Pages</dt>
                                                <dd class="text-gray-600">{{ book.pages }}</dd>
                                            </div>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="prose max-w-none mb-1">
                        <h2 class="text-2xl font-marketing font-bold text-primary mb-0.5">Book Description</h2>
                        <div data-book-description class="text-gray-700 space-y-0.5" v-html="book.description">
                        </div>
                    </div>

                    <div v-if="formattedGenres && formattedGenres.length > 0" class="mb-1">
                        <h3 class="text-lg font-marketing font-semibold text-primary mb-0.5">Related Genres</h3>
                        <div class="flex flex-wrap gap-0.5">
                            <span v-for="genre in formattedGenres" :key="genre"
                                class="px-4 py-2 bg-white text-primary rounded-full border border-gray-200 text-sm font-sans">
                                {{ genre.charAt(0).toUpperCase() + genre.slice(1) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="book.praise && book.praise.length > 0" class="mb-1">
                        <h2 class="text-2xl font-marketing font-bold text-primary mb-1">Praise</h2>
                        <div class="space-y-1">
                            <div v-for="(quote, index) in book.praise" :key="index"
                                class="border-l-4 border-accent pl-1.5">
                                <p class="text-gray-700 font-sans italic leading-relaxed mb-0.5">
                                    "{{ quote.text }}"
                                </p>
                                <p class="text-sm text-gray-600 font-sans font-semibold">
                                    —{{ quote.author }}
                                </p>
                            </div>
                        </div>
                    </div>


                    <div v-if="book.authorBio" class="mb-1">
                        <h2 class="text-2xl font-marketing font-bold text-primary mb-0.5">Author</h2>
                        <div class="flex flex-col xl:flex-row gap-1">
                            <div v-if="book.authorImage" class="flex-shrink-0">
                                <img :src="book.authorImage" :alt="book.author" class="w-32 h-32 object-cover" />
                            </div>
                            <div class="flex-1">
                                <h3 class="text-xl font-marketing font-bold text-primary mb-0.5" data-no-translate>
                                    {{ book.author }}
                                </h3>
                                <div class="text-gray-700 font-sans leading-relaxed" v-html="book.authorBio"></div>
                                <a :href="`/author/${encodeURIComponent(book.author)}`"
                                    class="mt-0.5 inline-block text-accent hover:text-green-600 font-marketing font-semibold underline">
                                    Learn More about <span data-no-translate>{{ book.author }}</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="relatedBooks && relatedBooks.length > 0" class="mt-2">
                <h2 class="text-3xl font-marketing font-bold text-primary mb-1">You May Also Like</h2>
                <div class="flex flex-wrap gap-1 justify-start">
                    <BookCard v-for="relatedBook in relatedBooks" :key="relatedBook.id" :book="relatedBook" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import BookCard from './BookCard.vue'
import Loader from './Loader.vue'
import { cartService } from '../services/cart'
import { getBookByIdDetailed, getAllBooks } from '../utils/books'
import { useBookImageStyle } from '../composables/useBookImageStyle'
import { useGenreFormat } from '../composables/useGenreFormat'
import { useLoadingState } from '../composables/useLoadingState'
import { useDataTranslation } from '../composables/useDataTranslation'
import { useBookDescriptionTranslation } from '../composables/useBookDescriptionTranslation'
import { translationService } from '../services/translation'

const { getBookImageStyle: getBookImageStyleUtil } = useBookImageStyle()
const { formatGenres } = useGenreFormat()
const { translateBookDescription } = useBookDescriptionTranslation()

const componentRef = ref(null)
const book = ref(null)
const loadError = ref('')
const { loading, setLoading } = useLoadingState(true)

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
    const bookId = getBookId()
    if (bookId && bookId !== 'book') {
        hasAttemptedLoad.value = false
        allBooks.value = await getAllBooks()
        await loadBook(bookId, true)
        if (book.value) {
            const currentGenres = book.value.genres || []
            relatedBooks.value = allBooks.value
                .filter(b => {
                    if (b.id === book.value.id) return false
                    const sameAuthor = b.author === book.value.author
                    const sharedGenres = b.genres && currentGenres.some(g => b.genres.includes(g))
                    return sameAuthor || sharedGenres
                })
                .slice(0, 5)
                .map(b => ({
                    id: b.id,
                    title: b.title,
                    author: b.author,
                    price: b.price,
                    coverImage: b.coverImage,
                    imageCropDimensions: b.imageCropDimensions || null
                }))
        }
    }
    await handleTranslation()
}

const selectedFormat = ref(null)
const expandedFormats = ref({})

// Import cart items directly
const cartItems = cartService.cartItems

const toggleFormat = (formatId) => {
    expandedFormats.value[formatId] = !expandedFormats.value[formatId]
}

const addFormatToCart = async (format) => {
    await cartService.addToCart(book.value, format)
}

// Free sample opens in the in-app reader/player (vendor-restricted, never a
// download). Audiobook samples stream in the player; everything else reads.
const sampleHref = (format) => {
    const base = (format.formatType || '').toLowerCase() === 'audiobook'
        ? '/listen/sample/'
        : '/read/sample/'
    return `${base}${format.digitalProductId}`
}

const isFormatInCart = computed(() => {
    return (format) => {
        if (!book.value || !format) return false
        const formatType = (format.formatType || format.type || '').toLowerCase()
        return cartItems.value.some(
            item => item.bookId === book.value.id && item.format === formatType
        )
    }
})

const getFormatQuantity = computed(() => {
    return (format) => {
        if (!book.value || !format) return 0
        const formatType = (format.formatType || format.type || '').toLowerCase()
        const item = cartItems.value.find(
            item => item.bookId === book.value.id && item.format === formatType
        )
        return item ? item.quantity : 0
    }
})

const incrementFormatQuantity = async (format) => {
    if (!book.value || !format) return
    const formatType = (format.formatType || format.type || '').toLowerCase()
    const item = cartItems.value.find(
        item => item.bookId === book.value.id && item.format === formatType
    )
    if (item) {
        await cartService.incrementQuantity(item.id)
    }
}

const decrementFormatQuantity = async (format) => {
    if (!book.value || !format) return
    const formatType = (format.formatType || format.type || '').toLowerCase()
    const item = cartItems.value.find(
        item => item.bookId === book.value.id && item.format === formatType
    )
    if (item) {
        await cartService.decrementQuantity(item.id)
    }
}


const allBooks = ref([])
const relatedBooks = ref([])
const hasAttemptedLoad = ref(false)

const getBookId = () => {
    const pathParts = window.location.pathname.split('/')
    return pathParts[pathParts.length - 1]
}

const loadBook = async (bookId, forceReload = false) => {
    if (hasAttemptedLoad.value && !forceReload) {
        return
    }
    setLoading(true)
    hasAttemptedLoad.value = true
    try {
        const found = await getBookByIdDetailed(bookId)
        if (found.book) {
            loadError.value = ''
            const foundBook = found.book
            const rawBook = {
                ...foundBook,
                description: foundBook.description || `<p>${foundBook.title} by ${foundBook.author}</p>`,
                praise: foundBook.praise || [],
                authorBio: foundBook.authorBio || '',
                authorImage: foundBook.authorImage || null
            }

            // Detect the description's language and translate to the user's
            // current language only when they differ.
            book.value = await translateBookDescription(rawBook, translationService.getLanguage())

            selectedFormat.value = book.value.formats?.[0] || { id: null, type: 'Hardcover', formatType: 'hardcover', price: book.value.price, originalPrice: book.value.price, discountPercentage: null, isbn: book.value.isbn || '', ebookUrl: null, audiobookUrl: null }

            // Available Formats always render expanded
            if (book.value.formats && book.value.formats.length > 0) {
                expandedFormats.value = {}
                book.value.formats.forEach(format => {
                    const formatId = format.id || format.type
                    expandedFormats.value[formatId] = true
                })
            }

            const currentGenres = book.value.genres || []
            relatedBooks.value = allBooks.value
                .filter(b => {
                    if (b.id === book.value.id) return false
                    const sameAuthor = b.author === book.value.author
                    const sharedGenres = b.genres && currentGenres.some(g => b.genres.includes(g))
                    return sameAuthor || sharedGenres
                })
                .slice(0, 5)
                .map(b => ({
                    id: b.id,
                    title: b.title,
                    author: b.author,
                    price: b.price,
                    coverImage: b.coverImage,
                    imageCropDimensions: b.imageCropDimensions || null
                }))

            window.scrollTo({ top: 0, behavior: 'smooth' })
        } else if (found.status === 'error') {
            book.value = null
            loadError.value = 'error'
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
            book.value = null
            loadError.value = 'not-found'
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    } catch (error) {
        console.error('Error loading book:', error)
        book.value = null
        loadError.value = 'error'
    } finally {
        setLoading(false)
    }
}

const reload = async () => {
    hasAttemptedLoad.value = false
    const bookId = getBookId()
    if (bookId && bookId !== 'book') {
        await loadBook(bookId, true)
    }
}

let checkInterval = null
let popStateHandler = null
let urlChangeHandler = null

const checkAndLoadBook = async () => {
    const bookId = getBookId()
    if (bookId && bookId !== 'book') {
        const parsedId = parseInt(bookId, 10)
        if (isNaN(parsedId) || parsedId <= 0) {
            return
        }
        const currentBookId = book.value?.id?.toString()
        if (bookId !== currentBookId) {
            hasAttemptedLoad.value = false
            await loadBook(bookId, true)
        }
    }
}

onMounted(async () => {
    window.addEventListener('language-changed', handleLanguageChange)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    allBooks.value = await getAllBooks()
    await checkAndLoadBook()
    await translatePage()
    if (book.value) {
        const currentGenres = book.value.genres || []
        relatedBooks.value = allBooks.value
            .filter(b => {
                if (b.id === book.value.id) return false
                const sameAuthor = b.author === book.value.author
                const sharedGenres = b.genres && currentGenres.some(g => b.genres.includes(g))
                return sameAuthor || sharedGenres
            })
            .slice(0, 5)
            .map(b => ({
                id: b.id,
                title: b.title,
                author: b.author,
                price: b.price,
                coverImage: b.coverImage,
                imageCropDimensions: b.imageCropDimensions || null
            }))
    }

    popStateHandler = () => {
        checkAndLoadBook()
    }
    window.addEventListener('popstate', popStateHandler)

    urlChangeHandler = async () => {
        await checkAndLoadBook()
    }
    window.addEventListener('book-navigation', urlChangeHandler)

    checkInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
            const currentBookId = getBookId()
            const currentParsedId = currentBookId && currentBookId !== 'book' ? parseInt(currentBookId, 10) : null
            if (currentParsedId && currentParsedId !== book.value?.id) {
                checkAndLoadBook()
            }
        }
    }, 200)
})

onUnmounted(() => {
    window.removeEventListener('language-changed', handleLanguageChange)
    if (popStateHandler) {
        window.removeEventListener('popstate', popStateHandler)
    }
    if (urlChangeHandler) {
        window.removeEventListener('book-navigation', urlChangeHandler)
    }
    if (checkInterval) {
        clearInterval(checkInterval)
        checkInterval = null
    }
})


const getBookImageStyle = () => {
    return getBookImageStyleUtil(book.value)
}

const formattedGenres = computed(() => {
    if (!book.value?.genres) {
        return []
    }
    return formatGenres(book.value.genres)
})

const hasMultipleFormats = computed(() => {
    return book.value?.formats && book.value.formats.length > 1
})

</script>

<style scoped>
:deep(ul) {
    list-style-type: disc;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

:deep(ol) {
    list-style-type: decimal;
    margin-left: 1.5em;
    margin-bottom: 0.1em;
}

:deep(li) {
    margin-bottom: 0.05em;
}
</style>
